/**
 * Schedule Query Modal
 * Modal for scheduling saved queries with delivery options
 */

import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { AppDialog } from '../components/dialog';

// Custom icon component for SVG icons
const SvgIcon = ({ src, alt, size = 20 }: { src: string; alt: string; size?: number }) => (
  <img 
    src={src} 
    alt={alt} 
    style={{ 
      width: size, 
      height: size, 
      display: 'inline-block',
      verticalAlign: 'middle'
    }} 
  />
);

interface ScheduleQueryModalProps {
  open: boolean;
  onClose: () => void;
  queryText: string;
  queryId: string;
}

type FrequencyType = 'daily' | 'weekly' | 'monthly' | 'custom';
type OutputFormat = 'chart' | 'csv' | 'both';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom (Cron)' },
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i % 12 || 12;
  const period = i < 12 ? 'AM' : 'PM';
  const time24 = i.toString().padStart(2, '0') + ':00';
  return { value: time24, label: `${hour}:00 ${period}` };
});

// Section header used inside the schedule modal — keeps each subgroup
// titled, scannable, and emoji-free.
function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box
        sx={(t: any) => ({
          flexShrink: 0,
          color: t.tokens.text.secondary,
          mt: 0.25,
          '& svg': { fontSize: 18 },
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ScheduleQueryModal({ open, onClose, queryText, queryId }: ScheduleQueryModalProps) {
  // Schedule settings
  const [frequency, setFrequency] = useState<FrequencyType>('daily');
  const [time, setTime] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [cronExpression, setCronExpression] = useState('0 9 * * *');
  
  // Delivery channels
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState(`Daily Report: ${queryText.substring(0, 50)}`);
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackChannel, setSlackChannel] = useState('');
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [teamsChannel, setTeamsChannel] = useState('');
  
  // Output format
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('chart');
  
  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [retryOnFailure, setRetryOnFailure] = useState(true);
  const [skipIfNoData, setSkipIfNoData] = useState(true);
  const [notifyOnFailure, setNotifyOnFailure] = useState(true);
  
  // Validation
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSchedule = async () => {
    // Validation
    if (!emailEnabled && !slackEnabled && !teamsEnabled) {
      setError('Please select at least one delivery channel');
      return;
    }
    
    if (emailEnabled && !emailRecipients.trim()) {
      setError('Please enter email recipients');
      return;
    }
    
    if (slackEnabled && !slackChannel.trim()) {
      setError('Please select a Slack channel');
      return;
    }
    
    if (teamsEnabled && !teamsChannel.trim()) {
      setError('Please select a Teams channel');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    
    try {
      // TODO: API call to create schedule
      const scheduleData = {
        saved_query_id: queryId,
        frequency,
        time,
        day_of_week: frequency === 'weekly' ? dayOfWeek : undefined,
        day_of_month: frequency === 'monthly' ? dayOfMonth : undefined,
        cron_expression: frequency === 'custom' ? cronExpression : undefined,
        delivery_channels: {
          email: emailEnabled ? {
            recipients: emailRecipients.split(',').map(e => e.trim()),
            subject: emailSubject,
          } : undefined,
          slack: slackEnabled ? {
            channel: slackChannel,
          } : undefined,
          teams: teamsEnabled ? {
            channel: teamsChannel,
          } : undefined,
        },
        output_format: outputFormat,
        advanced: {
          timezone,
          retry_on_failure: retryOnFailure,
          skip_if_no_data: skipIfNoData,
          notify_on_failure: notifyOnFailure,
        },
      };
      
      console.log('Schedule data:', scheduleData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onClose();
    } catch (err) {
      setError('Failed to create schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title="Schedule query"
      busy={isSubmitting}
      dividers
      primaryAction={{ label: 'Schedule', onClick: handleSchedule }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
        <Box sx={(t: any) => ({ mb: 3, p: 2, bgcolor: t.tokens.surface.sunken, borderRadius: `${t.ds.radius.sm}px`, border: `1px solid ${t.tokens.border.subtle}` })}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Query
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {queryText}
          </Typography>
        </Box>

        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <SectionHeader icon={<EventRepeatRounded />} title="Schedule frequency" />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Frequency</InputLabel>
              <Select
                value={frequency}
                label="Frequency"
                onChange={(e) => setFrequency(e.target.value as FrequencyType)}
              >
                {FREQUENCY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Run at</InputLabel>
              <Select
                value={time}
                label="Run at"
                onChange={(e) => setTime(e.target.value)}
              >
                {TIME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Conditional fields based on frequency */}
          {frequency === 'weekly' && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={dayOfWeek}
                label="Day of Week"
                onChange={(e) => setDayOfWeek(e.target.value)}
              >
                {DAYS_OF_WEEK.map(day => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {frequency === 'monthly' && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Day of Month</InputLabel>
              <Select
                value={dayOfMonth}
                label="Day of Month"
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
                <MenuItem value={-1}>Last day of month</MenuItem>
              </Select>
            </FormControl>
          )}
          
          {frequency === 'custom' && (
            <TextField
              fullWidth
              size="small"
              label="Cron Expression"
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              placeholder="0 9 * * *"
              helperText="Advanced: Use cron syntax (minute hour day month weekday)"
              sx={{ mt: 2 }}
            />
          )}
        </Box>

        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <SectionHeader
            icon={<SendRounded />}
            title="Delivery channels"
            subtitle="Select where to send results"
          />
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SvgIcon src="/icons/email.svg" alt="Email" />
                  <Typography variant="body2">Email</Typography>
                </Box>
              }
            />
            
            {emailEnabled && (
              <Box sx={{ ml: 4, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Recipients"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  placeholder="user@example.com, team@example.com"
                  helperText="Separate multiple emails with commas"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </Box>
            )}
          </Box>
          
          {/* Slack */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={slackEnabled}
                  onChange={(e) => setSlackEnabled(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SvgIcon src="/icons/slack.svg" alt="Slack" />
                  <Typography variant="body2">Slack</Typography>
                </Box>
              }
            />
            
            {slackEnabled && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Channel"
                  value={slackChannel}
                  onChange={(e) => setSlackChannel(e.target.value)}
                  placeholder="#analytics"
                  helperText="Enter channel name (e.g., #analytics)"
                />
              </Box>
            )}
          </Box>
          
          {/* Microsoft Teams */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={teamsEnabled}
                  onChange={(e) => setTeamsEnabled(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SvgIcon src="/icons/teams.svg" alt="Microsoft Teams" />
                  <Typography variant="body2">Microsoft Teams</Typography>
                </Box>
              }
            />
            
            {teamsEnabled && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Channel"
                  value={teamsChannel}
                  onChange={(e) => setTeamsChannel(e.target.value)}
                  placeholder="General"
                  helperText="Enter channel name (e.g., General)"
                />
              </Box>
            )}
          </Box>
          
        </Box>

        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <SectionHeader icon={<DescriptionOutlined />} title="Output format" />

          <RadioGroup value={outputFormat} onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}>
            <FormControlLabel
              value="chart"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>Chart + Summary (Recommended)</Typography>
                  <Typography variant="caption" color="text.secondary">Includes chart image and key insights</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="csv"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>Data Table (CSV)</Typography>
                  <Typography variant="caption" color="text.secondary">Full dataset as CSV attachment</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="both"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>Both</Typography>
                  <Typography variant="caption" color="text.secondary">Chart + CSV attachment</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Advanced settings (optional)
          </Button>
          
          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  label="Timezone"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="America/New_York">EST (UTC-5)</MenuItem>
                  <MenuItem value="America/Los_Angeles">PST (UTC-8)</MenuItem>
                  <MenuItem value="Europe/London">GMT (UTC+0)</MenuItem>
                  <MenuItem value="Asia/Kolkata">IST (UTC+5:30)</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={retryOnFailure}
                    onChange={(e) => setRetryOnFailure(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Retry on failure (up to 3 times)</Typography>}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipIfNoData}
                    onChange={(e) => setSkipIfNoData(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Skip if no data</Typography>}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyOnFailure}
                    onChange={(e) => setNotifyOnFailure(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Notification on failure</Typography>}
              />
            </Box>
          </Collapse>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
    </AppDialog>
  );
}
